import { signal } from '@preact/signals';
import { randomId } from '@/lib/id';

export type ToastVariant = 'info' | 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
  /** Internal: set when removeToast is called, triggers exit animation before purge. */
  dismissing?: boolean;
}

export const toasts = signal<Toast[]>([]);

// Match --anim-duration-normal in tokens.css. Toast exits use animate-slide-out-right.
const EXIT_ANIM_MS = 180;

function makeId(): string {
  return `toast-${randomId()}`;
}

export function pushToast(input: Omit<Toast, 'id' | 'dismissing'>): string {
  const id = makeId();
  const toast: Toast = { id, duration: 5000, ...input };
  toasts.value = [...toasts.value, toast];
  if (toast.duration && toast.duration > 0) {
    setTimeout(() => removeToast(id), toast.duration);
  }
  return id;
}

/** Marks the toast as dismissing so it can play its exit animation, then purges it. */
export function removeToast(id: string): void {
  const current = toasts.value.find((t) => t.id === id);
  if (!current || current.dismissing) return;
  toasts.value = toasts.value.map((t) => (t.id === id ? { ...t, dismissing: true } : t));
  setTimeout(() => {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }, EXIT_ANIM_MS);
}

export const toast = {
  info: (message: string, opts?: Omit<Toast, 'id' | 'variant' | 'message'>) =>
    pushToast({ variant: 'info', message, ...opts }),
  success: (message: string, opts?: Omit<Toast, 'id' | 'variant' | 'message'>) =>
    pushToast({ variant: 'success', message, ...opts }),
  error: (message: string, opts?: Omit<Toast, 'id' | 'variant' | 'message'>) =>
    pushToast({ variant: 'error', message, ...opts }),
  warning: (message: string, opts?: Omit<Toast, 'id' | 'variant' | 'message'>) =>
    pushToast({ variant: 'warning', message, ...opts }),
};
